<script>
import { mapGetters } from 'vuex';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import UpdateBanner from './components/app/UpdateBanner.vue';
import StatusBanner from './components/app/StatusBanner.vue';
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import PendingEmailVerificationBanner from './components/app/PendingEmailVerificationBanner.vue';
import PushPermissionBanner from './components/PushPermissionBanner.vue';
import vueActionCable from './helper/actionCable';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import { setColorTheme } from './helper/themeHelper';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import { useAccount } from 'dashboard/composables/useAccount';
import { useFontSize } from 'dashboard/composables/useFontSize';
import {
  registerSubscription,
  verifyServiceWorkerExistence,
} from './helper/pushHelper';
import ReconnectService from 'dashboard/helper/ReconnectService';
import { useUISettings } from 'dashboard/composables/useUISettings';

export default {
  name: 'App',

  components: {
    LoadingState,
    NetworkNotification,
    UpdateBanner,
    StatusBanner,
    PaymentPendingBanner,
    WootSnackbarBox,
    PendingEmailVerificationBanner,
    PushPermissionBanner,
  },
  setup() {
    const router = useRouter();
    const store = useStore();
    const { accountId } = useAccount();
    // Use the font size composable (it automatically sets up the watcher)
    const { currentFontSize } = useFontSize();
    const { uiSettings } = useUISettings();

    return {
      router,
      store,
      currentAccountId: accountId,
      currentFontSize,
      uiSettings,
    };
  },
  data() {
    return {
      latestChatwootVersion: null,
      reconnectService: null,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      isRTL: 'accounts/isRTL',
      currentUser: 'getCurrentUser',
      authUIFlags: 'getAuthUIFlags',
    }),
    hideOnOnboardingView() {
      return !isOnOnboardingView(this.$route);
    },
  },

  watch: {
    currentAccountId: {
      immediate: true,
      handler() {
        if (this.currentAccountId) {
          this.initializeAccount();
        }
      },
    },
  },
  mounted() {
    this.initializeColorTheme();
    this.listenToThemeChanges();
    // If user locale is set, use it; otherwise use account locale
    this.setLocale(
      this.uiSettings?.locale || window.chatwootConfig.selectedLocale
    );
  },
  unmounted() {
    if (this.reconnectService) {
      this.reconnectService.disconnect();
    }
  },
  methods: {
    initializeColorTheme() {
      setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = e => setColorTheme(e.matches);
    },
    setLocale(locale) {
      if (locale) {
        this.$root.$i18n.locale = locale;
      }
    },
    async initializeAccount() {
      await this.$store.dispatch('accounts/get');
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      const account = this.getAccount(this.currentAccountId);
      const { locale, latest_chatwoot_version: latestChatwootVersion } =
        account;
      const { pubsub_token: pubsubToken } = this.currentUser || {};
      // If user locale is set, use it; otherwise use account locale
      this.setLocale(this.uiSettings?.locale || locale);
      this.latestChatwootVersion = latestChatwootVersion;
      vueActionCable.init(this.store, pubsubToken);
      this.reconnectService = new ReconnectService(this.store, this.router);
      window.reconnectService = this.reconnectService;

      // Register or refresh the Web Push subscription:
      // - If the browser already has a subscription, re-send it to the server
      //   (idempotent on the backend) so that endpoints rotated by the browser
      //   are kept in sync.
      // - If there's no subscription yet but the user already granted permission
      //   (e.g. they accepted from another tab or a previous session), subscribe
      //   right away so they start receiving Web Push without further clicks.
      // - If permission is in 'default' state, do nothing here — the agent will
      //   be prompted from the in-app banner / Notification Preferences page.
      verifyServiceWorkerExistence(registration => {
        registration.pushManager.getSubscription().then(subscription => {
          if (subscription) {
            // eslint-disable-next-line no-console
            console.log('[push] existing subscription, re-registering');
            registerSubscription();
          } else if (
            'Notification' in window &&
            Notification.permission === 'granted'
          ) {
            // eslint-disable-next-line no-console
            console.log(
              '[push] permission granted but no subscription, subscribing now'
            );
            registerSubscription();
          } else {
            // eslint-disable-next-line no-console
            console.log(
              '[push] no subscription yet, permission =',
              'Notification' in window ? Notification.permission : 'unsupported'
            );
          }
        });
      });

      // Surface pending (unread) notifications when the user opens Chatwoot.
      // Useful for the case where they had the browser closed and missed pushes,
      // or where Web Push couldn't reach the device. The bell icon already shows
      // the count, but a one-time toast makes it impossible to miss.
      this.notifyPendingOnLoad();
    },
    async notifyPendingOnLoad() {
      try {
        await this.$store.dispatch('notifications/unReadCount');
        const unread = this.$store.getters['notifications/getUnreadCount'];
        // eslint-disable-next-line no-console
        console.log('[notifications] unread on load:', unread);
        if (!unread || unread <= 0) return;

        // Avoid showing the toast on every route change in the same session.
        const sessionKey = `cw_pending_alert_shown_${this.currentUser.id}`;
        if (window.sessionStorage.getItem(sessionKey)) return;
        window.sessionStorage.setItem(sessionKey, '1');

        const message =
          unread === 1
            ? this.$t('PENDING_NOTIFICATIONS.ONE')
            : this.$t('PENDING_NOTIFICATIONS.MANY', { count: unread });
        const action = {
          type: 'link',
          to: { name: 'notifications_index' },
          message: this.$t('PENDING_NOTIFICATIONS.OPEN'),
        };
        // useAlert via emitter; same path used elsewhere in the app
        const { emitter } = await import('shared/helpers/mitt');
        emitter.emit('newToastMessage', { message, action });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[notifications] failed to fetch unread count:', e);
      }
    },
  },
};
</script>

<template>
  <div
    v-if="!authUIFlags.isFetching"
    id="app"
    class="flex flex-col w-full h-screen min-h-0 bg-n-background"
    :dir="isRTL ? 'rtl' : 'ltr'"
  >
    <UpdateBanner :latest-chatwoot-version="latestChatwootVersion" />
    <StatusBanner />
    <template v-if="currentAccountId">
      <PendingEmailVerificationBanner v-if="hideOnOnboardingView" />
      <PaymentPendingBanner v-if="hideOnOnboardingView" />
    </template>
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
    <WootSnackbarBox />
    <NetworkNotification />
    <PushPermissionBanner />
  </div>
  <LoadingState v-else />
</template>

<style lang="scss">
@import './assets/scss/app';

.v-popper--theme-tooltip .v-popper__inner {
  background: black !important;
  font-size: 0.75rem;
  padding: 4px 8px !important;
  border-radius: 6px;
  font-weight: 400;
}

.v-popper--theme-tooltip .v-popper__arrow-container {
  display: none;
}
</style>
